import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
// The default export of hooks/useAxiosAuth is a FACTORY that builds a fresh
// axios instance, not a React hook — it calls nothing from React. Imported
// under its own name it trips eslint react-hooks/rules-of-hooks, because the
// rule reads `useX()` inside a non-component (a thunk body) as a hook called
// outside a render. Renaming it at the import is the whole fix.
import createAuthedAxios from "../hooks/useAxiosAuth";

// The imaging department, in one slice: the instruction, the machine, the slot,
// the exposure, and the statutory declaration that must exist BEFORE the
// exposure. Every path here belongs to api/routes/radiologyWorkflowRoutes.js,
// mounted at /api/radiology — one router, one slice, because it is one
// department: the clerk who books the machine, the radiographer who presses the
// button and the desk that cancels the scan are working the same list.
//
// THE REPORTING ACTS ARE HERE TOO, in their own section at the bottom: the
// template library, the draft, the signature and the amendment. They answer from
// two further routers — radiologyMasterRoutes and radiologyReportRoutes — mounted
// behind the SAME /api/radiology path as the workflow, exactly as the pharmacy and
// the laboratory split their route files for readability without splitting the
// module the user sees. One slice still, because to a radiologist the queue and
// the report are one screen's worth of work.
//
// WHAT IS STILL NOT HERE. Release (handing the report to the ward) and the PDF,
// which no screen in this repository calls yet, and the procedure and equipment
// masters, which their own pages reach through plain axios because they are CRUD
// tables with no shared state to hold. Nothing is guessed at: a thunk pointing at
// a path nobody has built answers 404, and a 404 reads to a radiologist as "the
// report I signed has vanished" rather than as "this button was written against an
// endpoint that was never merged".
//
// TENANCY. Every endpoint answers a question about ONE hospital's imaging
// department. A tenant admin, doctor or employee carries their hospital in the
// verified token and cannot name another, so the `tenantId` these thunks accept
// is ignored for them; Super Admin is deliberately unscoped everywhere in this
// codebase and MUST name the organisation or the request comes back 400.
// Passing it through unconditionally is what lets one screen serve both, instead
// of the pages growing a second code path that only Super Admin ever exercises.
//
// THE STATUS CHAIN THE SCREENS ARE DRAWING, so a reader of this file does not
// have to open the service to follow it:
//   ordered -> scheduled -> arrived -> performed -> reported -> signed -> released
// Nothing goes backwards, and 'cancelled' is reachable from every status BEFORE
// 'performed' and from none after it. The client never computes that: every read
// carries `nextStatuses` straight from services/radiology/imagingService.js, so
// a screen that draws a button the endpoint then refuses cannot happen by the
// two drifting apart.

const getSerializableError = (error) => {
    if (!error) return 'An unknown error occurred';
    if (typeof error === 'string') return error;
    if (error.response) {
        return error.response.data?.message || error.message || `Request failed with status code ${error.response.status}`;
    }
    return error.message || 'An error occurred';
};

/**
 * A rejection the SCREEN can act on, not merely display.
 *
 * A radiology refusal is almost never "something went wrong". It is "that MRI is
 * already holding R26-000123 at 14:40" (409 SLOT_TAKEN), "this unit's AERB
 * registration expired on 2026-03-31" (409 EQUIPMENT_AERB_EXPIRED), "this
 * machine is not registered under the PC-PNDT Act and this is an obstetric scan"
 * (409 EQUIPMENT_PNDT_UNREGISTERED), "the Form F has not been completed"
 * (409 PNDT_FORM_REQUIRED), or "14:42 is not on this machine's grid — the
 * nearest are 14:30 and 14:45" (400 SLOT_NOT_ON_GRID). Every one of those has a
 * different next action at the front desk, and a bare message string forces a
 * clerk with a patient in front of them to work it out from prose.
 *
 * NOTE THE FIELD NAME. This module's controllers hang their machine-readable
 * extras on `details` — studyId, accessionNo, equipmentId, aerbExpiry,
 * pndtExpiry, heldBy, slotMinutes, nextStatuses — where the laboratory's use
 * `data`. Copying labSlice's reader verbatim would silently return null for
 * every structured refusal this department raises, which reads on screen as a
 * plain red banner for exactly the cases the message was written to explain. So
 * `details` is read by name, and `detail` keeps the WHOLE body beside it, so a
 * screen can reach a field this file has never heard of without waiting for a
 * release of it.
 */
const rejectionDetail = (error) => ({
    error: getSerializableError(error),
    code: error?.response?.data?.code || null,
    permission: error?.response?.data?.permission || null,
    details: error?.response?.data?.details || null,
    detail: error?.response?.data || null,
});

// Empty values are dropped rather than sent as `?modality=`. The controllers
// guard with truthiness so an empty string is harmless today, but `?queue=` on a
// worklist is one refactor away from meaning "queue equals the empty string" and
// returning nothing, which reads on screen as "the department has no work
// outstanding" — a wrong answer delivered confidently, on the one screen whose
// whole job is to show what is still owed.
const query = (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        qs.append(key, Array.isArray(value) ? value.join(',') : String(value));
    });
    const s = qs.toString();
    return s ? `?${s}` : '';
};

// The same idea for a POST body. The controllers read the organisation as
// `req.body?.tenantId || req.query?.tenantId`, so a key that is present and empty
// is harmless TODAY — but it is also a claim ("this request names a tenant") that
// the body would be making falsely, and the next reader of a payload log has no
// way to tell it from a Super Admin who genuinely sent one. Absent means absent.
const withTenant = (body, tenantId) => (tenantId ? { ...body, tenantId } : body);

// ============================================================ orders ========

/**
 * A doctor's instruction, and one EMPTY study per procedure it names.
 *
 * A SCAN ORDER IS A ServiceOrder with orderType 'radiology' — there is no
 * parallel order table, because "what is still open for this patient" has to
 * cross departments to be worth asking.
 *
 * Body: { patientId, doctorId?, appointmentId?, priority?, procedureIds: [],
 * clinicalNotes?, tenantId? }, priority one of routine / urgent / stat.
 *
 * ONE PROCEDURE, ONE STUDY, AND THE SAME PROCEDURE TWICE MEANS TWO. Unlike a lab
 * panel, an imaging procedure does not expand: 'CT Brain plain' is one
 * examination on one machine at one time. A follow-up chest film the same evening
 * is a real second order and must not be de-duplicated by a screen trying to be
 * helpful.
 *
 * NOTHING IS BOOKED BY THIS CALL. The studies come back with no machine, no slot
 * and NO ACCESSION NUMBER — the number is minted at scheduling, so an order that
 * is never booked does not burn one and leave a hole in the year's register that
 * nobody can account for at an inspection. `data.studies[].pndtApplies` is the
 * one thing worth reading immediately: it says, before a slot is picked, that
 * this scan will need a Form F and a PC-PNDT-registered machine.
 *
 * Refused with NOTHING_TO_ORDER, UNKNOWN_PROCEDURE, PROCEDURE_WITHDRAWN (the
 * department has stopped doing it, commonly because the machine has gone),
 * UNKNOWN_PATIENT and PATIENT_NOT_IN_TENANT.
 */
export const createRadiologyOrder = createAsyncThunk(
    'radiology/createRadiologyOrder',
    async ({ tenantId = '', ...body } = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(`/radiology/orders`, withTenant(body, tenantId));
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// ========================================================== worklist ========

/**
 * WHAT THE DEPARTMENT STILL OWES, RIGHT NOW.
 *
 * A query, never a table. The moment "what is waiting" is stored rather than
 * derived it can disagree with the studies it claims to summarise, and a
 * department reading a stale queue leaves a patient in a gown in a corridor.
 *
 * THE ROW IS A STUDY, NOT AN ORDER — the one structural difference from the
 * laboratory's worklist. An order naming three examinations happens in three
 * rooms on three machines at three times, and the CT can be performed and
 * reported while the ultrasound has not started; a row per order would have to
 * describe that with one word, and the scan still owed is precisely the one
 * somebody has to chase.
 *
 * `queue` IS THE ACT THAT IS OWED, not a status invented for a dropdown:
 * to_schedule, to_perform (booked AND standing at the door — both are waiting to
 * be scanned), to_report, to_sign, to_release, released, cancelled. A bare study
 * status is also accepted, as are 'active' (everything still owed — the default)
 * and 'all'. `counts` comes back with BOTH breakdowns at once, byQueue and
 * byStatus, so the tabs can carry their badges without asking seven times.
 *
 * `date` CUTS THE LIST TO ONE CALENDAR DAY and — unlike the laboratory's — it is
 * honoured on every queue, because a radiology queue is anchored on a SLOT:
 * "everything booked on Tuesday" is the department's actual list rather than a
 * filter that hides its own backlog. With no range at all the window is the last
 * ninety days ANCHORED ON THE SLOT WHERE THERE IS ONE and on the order date where
 * there is not, so a scan booked for next Tuesday appears on Tuesday's list and a
 * scan ordered last week and never booked is still on this morning's scheduling
 * list, where somebody can finally give the patient a time.
 *
 * READ `truncated`. It means the window held more studies than the backend will
 * scan in one pass, so the list is a prefix of the truth — a silently short
 * worklist is one the department works to the end of and believes it is finished.
 *
 * Every row carries `pndtRequired` and `pndtCleared`, computed from the same rule
 * the console's refusal uses. The desk sees the Form F is missing while the
 * patient is still in the waiting room, instead of the radiographer discovering
 * it with them on the table.
 */
export const getRadiologyWorklist = createAsyncThunk(
    'radiology/getRadiologyWorklist',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const {
                queue = '', status = '', search = '', modality = '', equipmentId = '',
                date = '', from = '', to = '', page = 1, limit = 25, tenantId = '',
            } = params;
            const response = await axiosInstance.get(
                `/radiology/worklist${query({ queue, status, search, modality, equipmentId, date, from, to, page, limit, tenantId })}`
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// ========================================================= the grid =========

/**
 * ONE MACHINE, ONE DAY, EVERY SLOT WITH ITS AVAILABILITY.
 *
 * Params: { equipmentId (required), date?, studyId?, procedureId?, tenantId? }.
 * Pass `studyId` when booking a study that exists — it is the only form that
 * knows both the procedure AND the patient, which is what the PC-PNDT question
 * turns on, and it stops the study's OWN slot being reported as taken when it is
 * being moved (otherwise the only time a scan can be rescheduled to is the time
 * it is already in). `procedureId` is the weaker form, for a screen laying out
 * availability before a study exists.
 *
 * THE GRID AND THE REFUSAL COME FROM ONE FUNCTION, so a slot offered here cannot
 * be refused at the counter with the patient present. Two fields carry that:
 *   `blocked`  null when the machine may lawfully be used. Otherwise it is the
 *              exact refusal the booking endpoint would throw — an expired AERB
 *              registration, an unregistered PC-PNDT console, a unit under
 *              maintenance — and THE SCREEN MUST RENDER IT. An empty grid with no
 *              explanation reads as "fully booked", and nobody goes and chases
 *              the certificate.
 *   each slot  `available`, plus `reason` when it is not: 'equipment', 'taken'
 *              (with `heldBy` naming the accession holding it), 'overruns_close'
 *              or 'past'.
 *
 * `slot.start` IS THE VALUE A BOOKING MUST SEND BACK, unchanged. It is the exact
 * instant the partial unique index on (tenantId, equipmentId, scheduledAt)
 * compares, and a client that re-derives a time from the displayed 'HH:mm' can
 * produce an instant two minutes off the grid — which the index then admits as a
 * different value, and the MRI is double-booked.
 */
export const getSlots = createAsyncThunk(
    'radiology/getSlots',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const { equipmentId = '', date = '', studyId = '', procedureId = '', tenantId = '' } = params;
            const response = await axiosInstance.get(
                `/radiology/slots${query({ equipmentId, date, studyId, procedureId, tenantId })}`
            );
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// ======================================================== one study =========

/**
 * THE CONSOLE'S SCREEN AND THE RADIOLOGIST'S: the study, its images, its Form F,
 * its reports, and what may lawfully happen to it next.
 *
 * Whole rather than split, because every part of it is answering a question about
 * the same examination and splitting it invites a render where the patient header
 * has moved on and the images have not.
 *
 * WHAT A SCREEN MUST NOT RECOMPUTE, because all three come from the same
 * functions the write paths enforce:
 *   `study.nextStatuses`  the transition table's own answer.
 *   `equipmentProblem`    null when the machine may lawfully be used for THIS
 *                         examination on the day it is booked; otherwise the
 *                         refusal the perform endpoint would throw.
 *   `pndt`                { applies, cleared, form, history }. `applies` is
 *                         decided from the procedure and the patient — never
 *                         inferred from anything on the study row, which carries
 *                         nothing that means this — and `cleared` runs the same
 *                         three tests the console applies. `history` holds every
 *                         form ever written for the study, superseded ones
 *                         included, because "why are there two forms for this
 *                         scan" is a question an inspection asks.
 *
 * IMAGES ARRIVE AS PRESIGNED URLS AND THE KEYS DO NOT. A key is an address in a
 * private bucket; the URL expires in minutes. Nothing here should be persisted or
 * shared — a stored URL is a dead link tomorrow, which is why the column holds
 * keys in the first place. `transferComplete` is false when the study claims more
 * images than the archive is holding: a broken transfer, and the missing slices
 * are exactly where the lesion will be. A reporting screen shows that rather than
 * hiding it.
 */
export const getStudy = createAsyncThunk(
    'radiology/getStudy',
    async ({ studyId, tenantId = '' }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.get(`/radiology/studies/${studyId}${query({ tenantId })}`);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// ================================================== the study's life ========

/**
 * HOLD A MACHINE AND A SLOT — and give the scan the name it will be known by.
 *
 * Body: { studyId, equipmentId, scheduledAt, tenantId? }. `scheduledAt` must be a
 * slot's own `start` from getSlots, sent back unchanged: the time is pinned to
 * the machine's grid or REFUSED BY NAME (SLOT_NOT_ON_GRID, whose message names
 * the nearest legal starts), never rounded silently, because a patient told 14:40
 * who is booked at 14:30 arrives to find the machine occupied.
 *
 * THE ACCESSION NUMBER IS MINTED HERE, ONCE, AND THE CALLER MUST SHOW IT.
 * `data.accessionNo` is repeated at the top level of the answer for exactly that
 * reason. It is the name the machines, the archive, the film jacket and the bill
 * all know this scan by; a booking whose number never reached the screen is a
 * study nobody can file. A RESCHEDULE KEEPS THE NUMBER IT ALREADY HAS — the films
 * in the archive are stamped with it.
 *
 * WHAT THE SCREEN MUST HANDLE, none of it a generic error:
 *   409 SLOT_TAKEN — `details.heldBy` names the accession already in that slot.
 *       Offer the next free time; do not retry. A double-booked MRI is a patient
 *       sent home.
 *   409 EQUIPMENT_AERB_EXPIRED / EQUIPMENT_AERB_MISSING /
 *       EQUIPMENT_PNDT_EXPIRED / EQUIPMENT_PNDT_UNREGISTERED — the machine's
 *       paperwork, with the expiry date in `details`. This is the hospital's
 *       licence to operate the unit, not a warning banner, and the registration
 *       is checked against THE DAY OF THE SCAN rather than today's — so a
 *       fortnight of unlawful bookings cannot be taken this morning.
 *   409 EQUIPMENT_UNDER_MAINTENANCE / EQUIPMENT_RETIRED /
 *       EQUIPMENT_MODALITY_MISMATCH — the wrong machine for this examination.
 *   400 SLOT_OUTSIDE_HOURS / SLOT_OVERRUNS_CLOSE — the machine's day is the
 *       constraint, not the queue.
 *   409 SCHEDULE_RACED / ILLEGAL_TRANSITION — somebody else moved this study.
 *       Reload the worklist; do not resend.
 */
export const scheduleStudy = createAsyncThunk(
    'radiology/scheduleStudy',
    async ({ studyId, tenantId = '', ...body } = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(
                `/radiology/studies/${studyId}/schedule`,
                withTenant(body, tenantId)
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * The patient is physically in the department.
 *
 * ITS OWN ACT AND ITS OWN COLUMN even when the desk presses it a metre from the
 * scanner. The gap from scheduledAt to arrivedAt is the waiting the patient
 * experiences and the reason the 3 p.m. list is running at 5; the gap from there
 * to performedAt is the department's own throughput. One "handled at" time makes
 * both unmeasurable, and a department could never see that its overruns start at
 * the door rather than in the room.
 *
 * SKIPPING IT IS LEGAL AND MUST STAY THAT WAY. scheduled -> performed is a
 * transition the backend allows: a patient already standing at the console when
 * the booking is made never gets an arrival press, and a screen that forced one
 * would be recording a time nobody observed, which makes the waiting figure
 * worthless for everybody.
 */
export const arriveStudy = createAsyncThunk(
    'radiology/arriveStudy',
    async ({ studyId, tenantId = '' }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(
                `/radiology/studies/${studyId}/arrive`,
                withTenant({}, tenantId)
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * THE ONE THAT CANNOT BE UNDONE. A person was put through a machine.
 *
 * Body: { studyId, contrastUsed?, contrastAgent?, technique?, remarks?,
 * dicomStudyUid?, tenantId? }. Nothing in the transition table returns from
 * 'performed': un-performing a study would erase the fact that a patient was
 * exposed, and on a CT that is a dose of ionising radiation that was really
 * delivered. So a caller must confirm before sending this, and must never send it
 * speculatively or as a retry of a request whose answer was lost.
 *
 * WHO PERFORMED IT IS NEVER SENT FROM HERE. performedBy is taken from the
 * verified token — it is the radiographer half of radiology's two-person rule,
 * and a client-supplied name would be an attribution anybody could type.
 *
 * CONTRAST IS A FACT ABOUT A PERSON, not a copy of the procedure master's
 * planning flag. Sending contrastUsed without naming the agent is refused
 * (400 CONTRAST_AGENT_REQUIRED): iodinated contrast is nephrotoxic and gadolinium
 * is restricted in renal failure, and a reaction has to be traceable to what was
 * actually injected.
 *
 * THE REFUSAL THIS MODULE EXISTS FOR: 409 PNDT_FORM_REQUIRED. An obstetric or
 * pelvic ultrasound on a female patient may not be recorded as performed without
 * a completed Form F. Under the PC-PNDT Act that is imprisonment and the sealing
 * of the machine, not a fine — so the screen's response is to open the Form F,
 * never to offer a dismiss or an override. Also refused for a lapsed AERB or
 * PC-PNDT registration (checked AGAIN here, not only at booking, because a slot
 * taken three weeks ago is performed today and the certificate may have expired
 * in between), for ACTOR_UNKNOWN (sign out and back in — a performed study with
 * no radiographer has lost half the two-person rule permanently), and for
 * PERFORM_RACED.
 */
export const performStudy = createAsyncThunk(
    'radiology/performStudy',
    async ({ studyId, tenantId = '', ...body } = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(
                `/radiology/studies/${studyId}/perform`,
                withTenant(body, tenantId)
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * Record WHERE already-uploaded objects landed. THIS IS NOT AN UPLOAD ENDPOINT.
 *
 * Body: { studyId, imageKeys: [], imageCount?, dicomStudyUid?, tenantId? }. The
 * bytes reach storage by whatever path the department uses — a DICOM push, the
 * generic uploader, a bulk import — and this attaches their object KEYS. Never
 * URLs: a presigned read expires in minutes and the whole point of keeping an
 * image is the reprint in three years. A URL is refused as IMAGE_KEY_FOREIGN,
 * and so is a key belonging to another hospital, because the study read presigns
 * whatever is attached and a foreign key would turn it into a reader for another
 * hospital's head CT — which contains a recognisable face.
 *
 * SEND `imageCount` WHEN THE SOURCE STATES ONE, and send the source's number
 * rather than imageKeys.length. A study claiming 240 images while holding 180 is
 * a transfer that broke halfway; deriving the count from the array would make
 * that failure invisible by definition. `data.transferComplete` comes back false
 * in exactly that case, and a reporting screen must show it — a radiologist must
 * not report over a partial transfer.
 *
 * Refused with IMAGES_BEFORE_ACQUISITION until the study is recorded as
 * performed: pictures attached to a booking that has not happened belong to a
 * scan the record says never took place.
 */
export const attachStudyImages = createAsyncThunk(
    'radiology/attachStudyImages',
    async ({ studyId, tenantId = '', ...body } = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(
                `/radiology/studies/${studyId}/images`,
                withTenant(body, tenantId)
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * The scan is not going to happen.
 *
 * Body: { studyId, reason, tenantId? }. The reason is mandatory and is free text
 * rather than four options, because claustrophobia in the bore, a pregnancy
 * discovered at the door, a tube head down since Tuesday and a patient who never
 * came lead to four different conversations — with the ward, with engineering, or
 * with nobody. The row is KEPT with its reason: a scan that vanished without one
 * is indistinguishable from one quietly dropped to make a waiting list look
 * better, and the patient sent home is owed the answer.
 *
 * ALLOWED FROM EVERY STATUS BEFORE 'performed' AND FROM NONE AFTER IT. Once the
 * images exist the examination happened, and a study done in error is reported
 * and annotated, never erased. A screen must therefore offer cancel from the
 * study's own `nextStatuses` rather than from a rule of its own.
 *
 * Cancelling FREES THE SLOT — the machine is immediately bookable at that instant
 * again — so the schedule a screen is holding is stale the moment this returns.
 */
export const cancelStudy = createAsyncThunk(
    'radiology/cancelStudy',
    async ({ studyId, reason, tenantId = '' }, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(
                `/radiology/studies/${studyId}/cancel`,
                withTenant({ reason }, tenantId)
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// ============================================== PC-PNDT: the Form F =========

/**
 * THE STATUTORY DECLARATION, WRITTEN BEFORE THE SCAN.
 *
 * Body: { studyId, declarationAccepted: true, referredBy, referredByRegNo?,
 * performedByDoctorId?, performedByName?, performedByRegNo?, patientName?,
 * patientAge?, husbandOrFatherName?, address?, idProofType?, idProofNumber?,
 * gravida?, parity?, lastMenstrualPeriod?, gestationalAgeWeeks?, indication?,
 * findingsSummary?, tenantId? }.
 *
 * THREE THINGS ARE REFUSED SEPARATELY, BY NAME, and they are the same three the
 * console tests before it will record the scan:
 *   400 PNDT_DECLARATION_REQUIRED — the person conducting the procedure must
 *       accept that the sex of the foetus has not been detected or disclosed.
 *       Without it the row is not an incomplete form; it is not a form.
 *   400 PNDT_REFERRER_REQUIRED — who wanted this scan. The chain of
 *       accountability is the only thing standing between a lawful anomaly scan
 *       and a sex-selective one.
 *   409 PNDT_DOCTOR_NOT_REGISTERED — only a qualified, REGISTERED practitioner
 *       may conduct a pre-natal diagnostic procedure. The number is resolved from
 *       the same register the signing gate uses, and an explicit one in the body
 *       is honoured so a visiting sonologist who is not in this hospital's staff
 *       master can be recorded as themselves rather than under somebody else's
 *       name.
 * They are separate on purpose: "the form is incomplete" sends a radiographer
 * hunting through a long screen with a patient waiting.
 *
 * BEFORE THE SCAN IT IS EDITED (200); AFTER IT, IT IS SUPERSEDED (201 with
 * `data.supersededFormNo`). A declaration about a procedure that has taken place
 * is evidence, and evidence that can be rewritten in place is evidence of
 * nothing — so a correction inserts a new form and the old row's content is never
 * touched. `data.retainUntil` is two years from the STUDY's date, not today's:
 * the clock the Act starts is the procedure's, and a form completed a week late
 * must not quietly buy itself an extra week.
 *
 * A FORM IS NEVER REFUSED FOR BEING UNNECESSARY. If the procedure master has
 * isPndt unticked this still records the declaration — a wrongly-unticked
 * procedure is exactly the case where the form is the only evidence the
 * department did the right thing.
 */
export const saveFormF = createAsyncThunk(
    'radiology/saveFormF',
    async ({ studyId, tenantId = '', ...body } = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(
                `/radiology/studies/${studyId}/form-f`,
                withTenant(body, tenantId)
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// ========================================== masters: the templates ==========

/**
 * THE DEPARTMENT'S STOCK NORMAL REPORTS, for one procedure.
 *
 * Params: { procedureId?, modality?, tenantId? }. Answered from the masters
 * router; the body is `{ data: [{ templateId, name, technique, findings,
 * impression, isDefault }] }`.
 *
 * FETCHED PER PROCEDURE, NEVER ALL AT ONCE. A template library is every normal
 * report a department has ever agreed on, and a picker still holding the MRI
 * spine list while a chest film is open is a picker somebody eventually chooses
 * from by accident — which is a signed report describing an examination that was
 * not performed. `modality` is the looser fallback for a procedure that has no
 * templates of its own.
 *
 * A TEMPLATE CARRIES NO clinicalHistory AND NO recommendation, and the reporting
 * screen only ever copies technique / findings / impression out of one. Both of
 * the others are about THIS patient, and a stock sentence in either is precisely
 * the copy-paste accident templates were introduced to prevent.
 *
 * A FAILURE HERE IS NOT AN EMERGENCY. The caller falls back to an empty picker
 * rather than raising a page banner: the radiologist can still type the report,
 * which is what they did before templates existed.
 */
export const getTemplates = createAsyncThunk(
    'radiology/getTemplates',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const { procedureId = '', modality = '', tenantId = '' } = params;
            const response = await axiosInstance.get(
                `/radiology/templates${query({ procedureId, modality, tenantId })}`
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

// ================================================ reporting: the acts =======

/**
 * WRITE THE INTERPRETATION — or rewrite it. Nothing is signed by this call.
 *
 * Body: { studyId, clinicalHistory?, technique?, findings?, impression?,
 * recommendation?, tenantId? }. One draft per study: sending this again REPLACES
 * the text of the draft that is already there rather than stacking a second one,
 * so a radiologist who saves four times over an afternoon has one report at the
 * end of it and not four.
 *
 * A DRAFT IS NOT A RESULT. Nothing about it reaches the ward, and the caller must
 * keep saying so — a radiologist who believes "saved" meant "sent" is a chest film
 * with a pneumothorax on it that nobody was told about. Handing it over is
 * release, which is a separate act after the signature.
 *
 * Refused with STUDY_NOT_PERFORMED — there is nothing to interpret until the
 * images have been acquired — and with ILLEGAL_REPORT_TRANSITION once the live
 * report has been signed, because signed text is immutable and a correction is an
 * amendment (below), never an edit in place.
 */
export const draftReport = createAsyncThunk(
    'radiology/draftReport',
    async ({ studyId, tenantId = '', ...body } = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(
                `/radiology/studies/${studyId}/report`,
                withTenant(body, tenantId)
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * THE SIGNATURE — a named person putting their registration number to a reading.
 *
 * Body: { reportId, tenantId? }. NO NARRATIVE IS SENT. The text signed is the text
 * already stored, so the document that gets a signature is the one the signatory
 * read on screen; letting a client post the words alongside the signature would
 * let unsaved edits be signed without ever having been displayed as saved. The
 * reporting screen enforces the same rule from the other side by refusing to sign
 * while there are unsaved changes.
 *
 * WHO SIGNED IS NEVER SENT FROM HERE — it is resolved from the verified token by
 * imagingService.resolveSignatory, and the name and registration number are
 * stamped onto the row. A client-supplied signatory is an attribution anybody
 * could type, on the one document whose whole authority is whose name is on it.
 *
 * THE REFUSALS THE SCREEN BRANCHES ON, none of them "try again":
 *   409 SIGNATORY_NOT_REGISTERED / SIGNATORY_UNKNOWN — a radiology report carries
 *       the registration number of whoever signed it, so it cannot be signed
 *       without one. An administrator has to add it to the staff record; nothing
 *       the signatory types will get past it, and the draft is already safe.
 *   409 STUDY_NOT_PERFORMED — no images have been acquired, so there is nothing to
 *       put a signature to.
 *   409 ILLEGAL_REPORT_TRANSITION — somebody else moved this report while it was
 *       open. Reload; a blind retry would sign whatever it has since become.
 * The codes arrive on the rejection payload (see rejectionDetail), which is why
 * this screen dispatches through the slice rather than calling axios itself.
 */
export const signReport = createAsyncThunk(
    'radiology/signReport',
    async ({ reportId, tenantId = '' } = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(
                `/radiology/reports/${reportId}/sign`,
                withTenant({}, tenantId)
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * CORRECTING SOMETHING THAT HAS BEEN SIGNED — and possibly already read.
 *
 * Body: { reportId, amendReason, clinicalHistory?, technique?, findings?,
 * impression?, recommendation?, tenantId? }. Returns the NEW report, a draft at
 * version n+1, which has to be signed like any other.
 *
 * THIS OVERWRITES NOTHING. The signed report keeps its own text and is marked
 * superseded; the amendment is a new row beside it. A signed interpretation that
 * could be edited in place is evidence of nothing — and where the original was
 * released, a clinician is holding a copy of what it used to say, so the two
 * documents must both exist and must be tellable apart.
 *
 * `amendReason` IS THE CLINICALLY IMPORTANT HALF and is mandatory. It prints on
 * the amended report, and it is the only thing that tells a reader holding both
 * versions why they differ — "the left/right markers were transposed" and "the
 * nodule was measured again on the coronals" are very different messages to a
 * surgeon who has already booked a side.
 *
 * NOT THE DRAFTING RIGHT. Withdrawing a signed report is a signing-side act and
 * the endpoint is guarded accordingly; the reporting screen gates its own amend
 * affordance on radiology.report.sign to match, so the button and the endpoint
 * cannot disagree about who may do this.
 */
export const amendReport = createAsyncThunk(
    'radiology/amendReport',
    async ({ reportId, tenantId = '', ...body } = {}, { rejectWithValue }) => {
        const axiosInstance = createAuthedAxios();
        try {
            const response = await axiosInstance.post(
                `/radiology/reports/${reportId}/amend`,
                withTenant(body, tenantId)
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(rejectionDetail(error));
        }
    }
);

/**
 * The writes, listed rather than pattern-matched.
 *
 * staffSlice tests the action type against a regex, which works there because no
 * read is named after a write. Here `getStudy`, `scheduleStudy`, `performStudy`,
 * `arriveStudy` and `cancelStudy` all end in "Study", so /study/i would put the
 * whole console behind a saving spinner every time somebody merely opened a scan.
 * Naming the thunks is the version that cannot go wrong when the next one is
 * added.
 */
const MUTATIONS = [
    createRadiologyOrder,
    scheduleStudy, arriveStudy, performStudy, attachStudyImages, cancelStudy,
    saveFormF,
    // The reporting acts. `getTemplates` is deliberately absent — it is a read,
    // and listing it here would raise the shared `mutating` flag every time a
    // study is opened, which is the flag a screen disables its Save button on.
    draftReport, signReport, amendReport,
];
const MUTATION_TYPES = new Set(MUTATIONS.map((thunk) => thunk.typePrefix));
const matchMutation = (phase) => (action) => {
    const cut = action.type.lastIndexOf('/');
    return cut > 0 && action.type.slice(cut + 1) === phase && MUTATION_TYPES.has(action.type.slice(0, cut));
};

const radiologySlice = createSlice({
    name: "radiology",
    initialState: {
        // ---- the work in progress ----
        worklist: [],
        // Which queue the rows on screen belong to, as the BACKEND resolved it —
        // 'active' when nothing was asked for, the queue id or the bare status
        // otherwise. Kept because the answer can differ from what was requested,
        // and a tab highlighted from the request rather than the answer lies
        // about which list is being shown.
        worklistQueue: 'active',
        // Both breakdowns at once: { byStatus, byQueue }. Kept whole rather than
        // unpacked, because the tabs need every badge on screen at all times and
        // "four studies waiting to be reported" is the sentence that decides how
        // the next hour is spent.
        worklistCounts: { byStatus: {}, byQueue: {} },
        worklistTotal: 0,
        worklistTotalPages: 1,
        // The window held more studies than the backend scans in one pass, so
        // this list is a PREFIX of the truth. Surfaced rather than swallowed: a
        // silently short worklist is one the department works to the end of and
        // believes it is finished.
        worklistTruncated: false,
        worklistLoading: false,

        // ---- one machine's day ----
        // The whole grid as the service laid it out: equipment, date, openTime,
        // closeTime, slotMinutes, durationMins, `blocked` and `slots`. Held whole
        // because `blocked` and the slots are one answer — an empty-looking grid
        // separated from the reason it is empty reads as "fully booked".
        slots: null,
        slotsLoading: false,

        // ---- one study, whole ----
        // { study, images, imagesHeld, transferComplete, patient, procedure,
        //   equipment, equipmentProblem, order, pndt, reports }. Held as the
        //   server sent it: every part answers a question about the same
        //   examination, and splitting it invites a render where the patient
        //   header has moved on and the images have not.
        selectedStudy: null,
        studyLoading: false,

        // What the last booking minted. THE ACCESSION NUMBER IS KEPT, not
        // toasted — it goes on the film jacket and the request form, and a toast
        // disappears while the jacket is still blank.
        lastSchedule: null,
        // What the last exposure recorded: { study, accessionNo, orderStatus,
        // pndtFormNo }.
        lastPerform: null,
        // The last attachment's claim against what the archive is holding. A
        // radiologist must not report over a broken transfer, so the count and
        // `transferComplete` survive the toast that announced them.
        lastAttachment: null,
        // The Form F as filed, with `supersededFormNo` when it replaced one.
        lastFormF: null,
        // The order just raised, with one empty study per procedure. Each carries
        // `pndtApplies`, which is what lets the booking screen warn before a slot
        // is picked rather than at the console.
        lastOrder: null,

        // Kept apart from `error`, and apart from each other, because they are
        // structured answers a screen ACTS on rather than banners to dismiss —
        // and the actions differ. A refused booking offers another slot or sends
        // somebody to chase a certificate; a refused exposure opens the Form F.
        // See rejectionDetail.
        scheduleError: null,
        performError: null,

        mutating: false,
        error: null,
    },
    reducers: {
        clearRadiologyError: (state) => { state.error = null; },
        clearScheduleError: (state) => { state.scheduleError = null; },
        clearPerformError: (state) => { state.performError = null; },
        // The accession number has been written on the jacket.
        clearLastSchedule: (state) => { state.lastSchedule = null; },
        // Leaving one study's console for another. The worklist and the counts
        // survive on purpose — they are the list being worked, and refetching
        // them makes the screen blink for no reason.
        resetStudyView: (state) => {
            state.selectedStudy = null;
            state.slots = null;
            state.lastSchedule = null;
            state.lastPerform = null;
            state.lastAttachment = null;
            state.lastFormF = null;
            state.scheduleError = null;
            state.performError = null;
        },
        resetRadiologyState: (state) => {
            state.worklist = [];
            state.worklistQueue = 'active';
            state.worklistCounts = { byStatus: {}, byQueue: {} };
            state.worklistTotal = 0;
            state.worklistTotalPages = 1;
            state.worklistTruncated = false;
            state.slots = null;
            state.selectedStudy = null;
            state.lastSchedule = null;
            state.lastPerform = null;
            state.lastAttachment = null;
            state.lastFormF = null;
            state.lastOrder = null;
            state.scheduleError = null;
            state.performError = null;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // ---- worklist ----
            // The rows are NOT cleared on pending. This list refreshes itself on
            // a screen the department keeps open all day, and emptying it between
            // polls would make the whole queue blink — which is how people stop
            // reading it. A caller that wants a first-load spinner should gate it
            // on an empty list, not on this flag.
            .addCase(getRadiologyWorklist.pending, (state) => { state.worklistLoading = true; state.error = null; })
            .addCase(getRadiologyWorklist.fulfilled, (state, action) => {
                state.worklistLoading = false;
                state.worklist = action.payload?.data || [];
                state.worklistQueue = action.payload?.queue || 'active';
                state.worklistCounts = action.payload?.counts || { byStatus: {}, byQueue: {} };
                state.worklistTotal = action.payload?.total || 0;
                state.worklistTotalPages = action.payload?.totalPages || 1;
                state.worklistTruncated = action.payload?.truncated === true;
            })
            .addCase(getRadiologyWorklist.rejected, (state, action) => {
                state.worklistLoading = false;
                state.error = action.payload?.error || 'Failed to load the radiology worklist';
            })

            // ---- the grid ----
            .addCase(getSlots.pending, (state) => {
                state.slotsLoading = true;
                // The previous machine's day goes before the next one arrives.
                // Leaving it up means a clerk can book a patient onto Tuesday's
                // free 14:40 on a scanner whose grid is no longer on screen.
                state.slots = null;
                state.error = null;
            })
            .addCase(getSlots.fulfilled, (state, action) => {
                state.slotsLoading = false;
                state.slots = action.payload || null;
            })
            .addCase(getSlots.rejected, (state, action) => {
                state.slotsLoading = false;
                state.error = action.payload?.error || 'Failed to load the schedule';
            })

            // ---- one study ----
            .addCase(getStudy.pending, (state) => {
                state.studyLoading = true;
                // The previous study is dropped before the next one arrives.
                // Leaving it up means somebody can record an exposure, or read a
                // head CT, under another patient's name for as long as the
                // request takes.
                state.selectedStudy = null;
                state.error = null;
            })
            .addCase(getStudy.fulfilled, (state, action) => {
                state.studyLoading = false;
                state.selectedStudy = action.payload || null;
            })
            .addCase(getStudy.rejected, (state, action) => {
                state.studyLoading = false;
                state.error = action.payload?.error || 'Failed to load this study';
            })

            // ---- orders ----
            .addCase(createRadiologyOrder.fulfilled, (state, action) => {
                state.lastOrder = action.payload?.data || null;
            })

            // ---- the study's life ----
            .addCase(scheduleStudy.pending, (state) => { state.scheduleError = null; })
            .addCase(scheduleStudy.fulfilled, (state, action) => {
                state.lastSchedule = action.payload?.data || null;
                // The grid on screen is stale the instant a slot is taken —
                // including by this very booking. Dropping it stops the next
                // click being made against a picture of a machine that was free a
                // moment ago.
                state.slots = null;
            })
            .addCase(scheduleStudy.rejected, (state, action) => { state.scheduleError = action.payload || null; })

            .addCase(performStudy.pending, (state) => { state.performError = null; })
            .addCase(performStudy.fulfilled, (state, action) => {
                state.lastPerform = action.payload?.data || null;
            })
            // PNDT_FORM_REQUIRED arrives here. Kept structured rather than
            // flattened into `error`: the screen's answer to it is to open the
            // Form F, never to show a banner somebody can dismiss.
            .addCase(performStudy.rejected, (state, action) => { state.performError = action.payload || null; })

            .addCase(attachStudyImages.fulfilled, (state, action) => {
                state.lastAttachment = action.payload?.data || null;
            })

            .addCase(cancelStudy.fulfilled, (state) => {
                // A cancelled study releases the machine at that instant, so any
                // grid being held is wrong about what is free.
                state.slots = null;
            })

            // ---- Form F ----
            .addCase(saveFormF.fulfilled, (state, action) => {
                state.lastFormF = action.payload?.data || null;
            })

            // Every mutation shares one flag; the pages refetch on success. The
            // two structured refusals above are set in their own cases and are
            // deliberately ALSO recorded here — a screen that renders only
            // `scheduleError` still needs `mutating` to fall back to false, and
            // one that renders only `error` still shows something rather than
            // failing silently.
            .addMatcher(matchMutation('pending'), (state) => { state.mutating = true; state.error = null; })
            .addMatcher(matchMutation('fulfilled'), (state) => { state.mutating = false; })
            .addMatcher(matchMutation('rejected'), (state, action) => {
                state.mutating = false;
                state.error = action.payload?.error || 'Action failed';
            });
    },
});

export const {
    clearRadiologyError,
    clearScheduleError,
    clearPerformError,
    clearLastSchedule,
    resetStudyView,
    resetRadiologyState,
} = radiologySlice.actions;
export default radiologySlice.reducer;
